import {
  buildGoogleAdsUserData,
  sanitizeStripeBillingDetails,
  type GoogleAdsGeoData,
  type GoogleAdsUserData,
  type SanitizedStripeBillingDetails,
} from './googleAdsUserData';
import { resolvePLTVPriceForFlow } from './ltvCalculator';

export type GoogleAdsPaymentProcessor = 'stripe' | 'braintree';

export interface GoogleAdsPrimaryPurchaseContext {
  subscriptionId: string;
  processor: GoogleAdsPaymentProcessor;
  paymentMethod: string;
  stripeBillingDetails: SanitizedStripeBillingDetails | null;
}

export interface PrimaryPurchaseEventDefaults {
  plan: string;
  currency?: string;
  fallbackPrice: number;
}

export interface PrimaryPurchaseCompletionDefaults extends PrimaryPurchaseEventDefaults {
  email?: string | null;
  geo?: GoogleAdsGeoData | null;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  flowType?: string | null;
  landing?: string | null;
  userLocation?: string | null;
  useTimerDefaultLTV?: boolean;
}

export interface PrimaryPurchaseCompletionInput {
  subscriptionId: string;
  eventId: string;
  chargedPrice?: number;
  currency?: string;
  processor: GoogleAdsPaymentProcessor;
  paymentMethod: string;
  ltvPaymentMethod?: string;
  profileName?: string | null;
  stripeBillingDetails?: SanitizedStripeBillingDetails | null;
}

export interface PrimaryPurchaseEventData {
  price: number;
  currency?: string;
  plan: string;
  sub_id: string;
  event_id: string;
  gclid?: string;
  pixelName: string;
  flowType?: string | null;
  landing?: string | null;
  pLTVPrice?: number;
  userLocation?: string | null;
  userData?: GoogleAdsUserData;
}

export interface PrimaryPurchaseCompletion {
  googleAdsContext: GoogleAdsPrimaryPurchaseContext;
  purchaseEventData: PrimaryPurchaseEventData;
}

export const buildPrimaryPurchaseCompletion = ({
  defaults,
  input,
}: {
  defaults: PrimaryPurchaseCompletionDefaults;
  input: PrimaryPurchaseCompletionInput;
}): PrimaryPurchaseCompletion => {
  const stripeBillingDetails =
    input.processor === 'stripe'
      ? sanitizeStripeBillingDetails(input.stripeBillingDetails)
      : undefined;

  const userData = buildGoogleAdsUserData({
    email: defaults.email,
    profileName: input.profileName,
    stripeBillingDetails,
    geo: defaults.geo,
  });

  const pLTVPrice = resolvePLTVPriceForFlow({
    landing: defaults.landing,
    plan: defaults.plan,
    paymentMethod: input.ltvPaymentMethod ?? input.paymentMethod,
    userLocation: defaults.userLocation,
    useTimerDefault: defaults.useTimerDefaultLTV,
  });

  const pixelName = defaults.fbclid ? 'fb' : defaults.ttclid ? 'ttq' : '';

  return {
    googleAdsContext: {
      subscriptionId: input.subscriptionId,
      processor: input.processor,
      paymentMethod: input.paymentMethod,
      stripeBillingDetails: stripeBillingDetails ?? null,
    },
    purchaseEventData: {
      price: input.chargedPrice ?? defaults.fallbackPrice,
      currency: input.currency ?? defaults.currency,
      plan: defaults.plan,
      sub_id: input.subscriptionId,
      event_id: input.eventId,
      gclid: defaults.gclid,
      pixelName,
      flowType: defaults.flowType,
      landing: defaults.landing,
      ...(pLTVPrice !== undefined && { pLTVPrice }),
      userLocation: defaults.userLocation,
      ...(userData && { userData }),
    },
  };
};
